import { CheckCircle } from '@mui/icons-material';
import { Button, CardContainer, Tag } from 'finara-components';

import { BulkPaymentResult } from '@/types/payment';

interface BulkResultsProps {
  results: BulkPaymentResult;
  onFinish: () => void;
}

export const BulkResults: React.FC<BulkResultsProps> = ({ results, onFinish }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(amount);
  };

  const successfulAmount = results.payments
    .filter(p => p.estado === 'exitoso')
    .reduce((sum, payment) => sum + payment.monto, 0);

  const scheduledAmount = results.payments
    .filter(p => p.estado === 'programado')
    .reduce((sum, payment) => sum + payment.monto, 0);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(date);
    } catch {
      return '-';
    }
  };

  return (
    <div className="max-w-4xl mx-auto text-center">
      {/* Success Icon */}
      <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
        <CheckCircle className="text-green-400" style={{ fontSize: '3rem' }} />
      </div>

      {/* Success Message */}
      <h2 className="text-3xl font-bold text-brand-white mb-2">Ejecución Completada</h2>
      <p className="text-muted mb-8">El lote de pagos ha sido procesado</p>

      {/* Results Summary */}
      <div className="grid grid-cols-1 md:!grid-cols-4 gap-6 mb-8">
        <CardContainer className="p-6 text-center">
          <div className="text-3xl font-bold text-green-600 mb-2">{results.successful}</div>
          <div className="text-sm text-muted">Exitosos</div>
        </CardContainer>

        <CardContainer className="p-6 text-center">
          <div className="text-3xl font-bold text-blue-600 mb-2">{results.scheduled}</div>
          <div className="text-sm text-muted">Programados</div>
        </CardContainer>

        <CardContainer className="p-6 text-center">
          <div className="text-3xl font-bold text-red-600 mb-2">{results.failed}</div>
          <div className="text-sm text-muted">Fallidos</div>
        </CardContainer>

        <CardContainer className="p-6 text-center">
          <div className="text-3xl font-bold text-brand-white mb-2">
            {formatCurrency(successfulAmount)}
          </div>
          <div className="text-sm text-muted">Monto Procesado</div>
        </CardContainer>
      </div>

      {/* Scheduled Amount Info */}
      {results.scheduled > 0 && scheduledAmount > 0 && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
          <div className="text-sm text-brand-white">
            <span className="font-semibold">{formatCurrency(scheduledAmount)}</span> en pagos
            programados se procesarán en las fechas indicadas.
          </div>
        </div>
      )}

      {/* Results Table */}
      <CardContainer className="mb-8 text-left">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-brand-white mb-4">Resultados del Procesamiento</h3>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">
                    Beneficiario
                  </th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">Monto</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">Estado</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">
                    Fecha Programada
                  </th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">
                    Referencia
                  </th>
                </tr>
              </thead>
              <tbody>
                {results.payments.map((payment, index) => (
                  <tr key={index} className="border-b border-border">
                    <td className="py-3 px-2 text-sm">{payment.beneficiario}</td>
                    <td className="py-3 px-2 text-sm">{formatCurrency(payment.monto)}</td>
                    <td className="py-3 px-2">
                      {payment.estado === 'exitoso' && <Tag variant="green">Exitoso</Tag>}
                      {payment.estado === 'programado' && <Tag variant="blue">Programado</Tag>}
                      {payment.estado === 'fallido' && <Tag variant="red">Fallido</Tag>}
                    </td>
                    <td className="py-3 px-2 text-sm text-muted">
                      {payment.estado === 'programado' ? formatDate(payment.scheduledDate) : '-'}
                    </td>
                    <td className="py-3 px-2 text-sm font-mono text-muted">
                      {payment.referencia || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </CardContainer>

      {/* Action Button */}
      <Button variant="black" size="large" onClick={onFinish}>
        Finalizar
      </Button>
    </div>
  );
};
